import posthog from 'posthog-js';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import React, { useEffect, useMemo } from 'react';

import { useNamedLogger } from '@eluve/logger';
import { bootstrappedFlags } from '@eluve/posthog';
import { useUserIdFromSession } from '@eluve/session-helpers';

import {
  AnalyticsInstance,
  CoreAnalyticsProvider,
} from './CoreAnalyticsProvider';
import { POSTHOG_ANONYMOUS_ID_KEY } from './constants';

export interface AnalyticsProviderProps {
  children: React.ReactNode;
  apiKey: string;
  apiHost: string;
  disableExternalDependencies?: boolean;
}

/**
 * Inner component that runs inside PostHogProvider to access the PostHog instance
 * and provide it to CoreAnalyticsProvider
 */
const CoreAnalyticsProviderBridge: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const postHogInstance = usePostHog();

  const analyticsInstance = useMemo<AnalyticsInstance>(() => {
    return {
      capture: postHogInstance.capture.bind(postHogInstance),
      isFeatureEnabled: (flagKey) => postHogInstance.isFeatureEnabled(flagKey),
      getFeatureFlagPayload: (flagKey) =>
        postHogInstance.getFeatureFlagPayload(flagKey),
    };
  }, [postHogInstance]);

  return (
    <CoreAnalyticsProvider analyticsInstance={analyticsInstance}>
      {children}
    </CoreAnalyticsProvider>
  );
};

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  apiKey,
  apiHost,
  disableExternalDependencies = false,
}) => {
  const logger = useNamedLogger('AnalyticsProvider');
  const userId = useUserIdFromSession();
  const [posted, setPosted] = React.useState(false);

  const postHogConfig = {
    bootstrap: {
      distinctID: userId,
      ...bootstrappedFlags,
    },
    api_host: apiHost,
    autocapture: false,
    person_profiles: 'identified_only' as const,
    ...(disableExternalDependencies && {
      disable_external_dependency_loading: true,
    }),
    session_recording: {
      maskTextSelector: '.privacy-text, .tiptap *, .posthog-privacy-text',
    },
  };

  useEffect(() => {
    if (!userId || posted) return;

    const storedPostHogId = localStorage.getItem(POSTHOG_ANONYMOUS_ID_KEY);
    const currentPostHogId = posthog.get_distinct_id();

    if (!storedPostHogId) return;

    if (storedPostHogId !== currentPostHogId) {
      posthog.alias(storedPostHogId, userId);
    }

    setPosted(true);
    localStorage.removeItem(POSTHOG_ANONYMOUS_ID_KEY);
  }, [logger, posted, userId]);

  // Reuse existing PostHog client if already initialized
  if (posthog.__loaded) {
    posthog.set_config(postHogConfig);

    if (userId) {
      posthog.identify(userId);
    }

    return (
      <PostHogProvider client={posthog}>
        <CoreAnalyticsProviderBridge>{children}</CoreAnalyticsProviderBridge>
      </PostHogProvider>
    );
  }

  return (
    <PostHogProvider
      apiKey={apiKey}
      options={{
        ...postHogConfig,
        loaded: () => {
          logger.info('Posthog loaded');
        },
      }}
    >
      <CoreAnalyticsProviderBridge>{children}</CoreAnalyticsProviderBridge>
    </PostHogProvider>
  );
};
